import {
  AbsoluteRoutes,
  LocalStorageKeys,
  LocalStorageService,
  NotificationType,
  post,
  useAddNotification,
  useGetUserProfile,
  useTranslation,
} from '../../../utils';
import type { TTimeSelectorHandlersProps } from '../../../utils/types';
import { activeRequestsAtom, draftApptAtom, fullDataAtom } from '../../../atoms';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export function useHandlers({ facilityId, value }: TTimeSelectorHandlersProps) {
  const navigate = useNavigate();

  const pushNotification = useAddNotification();
  const { getUserData } = useGetUserProfile();
  const { t } = useTranslation();

  const setActiveRequests = useSetRecoilState(activeRequestsAtom);
  const setDraftAppt = useSetRecoilState(draftApptAtom);
  const fullExpertData = useRecoilValue(fullDataAtom);

  const createAppointment = useCallback(async () => {
    const phoneNumber = LocalStorageService.getItem(LocalStorageKeys.PhoneNumber);
    const token = LocalStorageService.getItem(LocalStorageKeys.AccessToken);

    if (!value) return;

    if (!token && !!fullExpertData) {
      setDraftAppt({ ...value, facilityId });
      pushNotification({
        type: NotificationType.Success,
        title: t('Confirmation.Text_client_appointment'),
        text: t('Web.Msg_confirm_draft_appt'),
      });
      navigate(AbsoluteRoutes.login);
      return;
    }

    const userData = await getUserData();
    if (!userData || !fullExpertData) return;

    setActiveRequests(prev => prev + 1);

    const apptData = {
      clientId: userData.id,
      clientPhoneNumber: phoneNumber,
      comment: '',
      expertFacilityId: facilityId,
      serviceProviderId: fullExpertData.expertProfile.id,
      startDate: value.startsUTC,
      endDate: value.endsUTC,
      userName: userData.userName,
    };

    const { data, error } = await post('CREATE_APPOINTMENT', apptData);

    setActiveRequests(prev => prev - 1);

    if (!!error) {
      console.log('Booking Error:', error);
    } else if (!!data) {
      // The expert page is already 2 entries back in history (Expert -> Services -> Booking),
      // so go back to it instead of pushing a new entry — otherwise the back button from here
      // would step forward through Services/Booking again instead of reaching the experts list.
      navigate(-2);
    }

    pushNotification({
      type: !!data ? NotificationType.Success : NotificationType.Error,
      title: t('Confirmation.Text_client_appointment'),
      text: !!data ? t('Confirmation.Text_success_expert') : t('Push_notification_msg.Cannot_create_appt_web'),
    });
  }, [
    facilityId,
    fullExpertData,
    getUserData,
    navigate,
    pushNotification,
    setActiveRequests,
    setDraftAppt,
    t,
    value,
  ]);

  return { createAppointment };
}